import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { addDoc, collection, getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useEffect, useMemo, useState } from "react";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { ProjectRole, projectDocumentSchema } from "../../types/firestore";
import { getFirestoreZodConverter } from "../../modules/firestoreZodConverter";
import { FirebaseError } from "firebase/app";
import firebaseErrors from "../../modules/firebaseErrors";
import Spinner from "react-bootstrap/Spinner";

const unlockCode = "MG-925E8C";

const auth = getAuth();
const firestore = getFirestore();

export default function Create() {
	const navigate = useNavigate();

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string>();

	const [user] = useAuthState(auth);

	const [showCreateProject, setShowCreateProject] = useState(false);

	const [projectName, setProjectName] = useState("");
	const [unlockKey, setUnlockKey] = useState("");

	const projectsCollectionRef = useMemo(
		() =>
			collection(firestore, "Projects").withConverter(
				getFirestoreZodConverter(projectDocumentSchema),
			),
		[],
	);

	useEffect(() => {
		if (!showCreateProject) return;
		setProjectName("");
		setUnlockKey("");
		setError(undefined);
	}, [showCreateProject]);

	if (!user) return <div></div>;

	return (
		<div>
			<Button variant="success" onClick={() => setShowCreateProject(true)}>
				<i className="bi bi-plus-circle" /> Projekt erstellen
			</Button>
			<Modal
				show={showCreateProject}
				onHide={() => !saving && setShowCreateProject(false)}
				backdrop="static"
				keyboard={false}>
				<Modal.Header closeButton>
					<Modal.Title>Neues Projekt</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FloatingLabel label="Projektname" className="mb-2">
						<Form.Control
							placeholder="Projektname"
							value={projectName}
							onChange={(e) => setProjectName(e.target.value)}
						/>
					</FloatingLabel>
					<FloatingLabel label="Freischaltcode" className="mb-2">
						<Form.Control
							placeholder="Freischaltcode"
							value={unlockKey}
							onChange={(e) => setUnlockKey(e.target.value)}
						/>
					</FloatingLabel>
					{!unlockKey && (
						<div className="text-muted mb-2">
							Du benötigst zurzeit einen Freischaltcode, um Projekte zu erstellen.
						</div>
					)}
					{unlockKey && unlockKey !== unlockCode && (
						<div className="text-danger mb-2">Der eingegebene Freischaltcode existiert nicht.</div>
					)}

					{error && <div className="mb-2 text-danger">{error}</div>}
				</Modal.Body>
				<Modal.Footer>
					<Button
						disabled={!showCreateProject || saving}
						variant="danger"
						onClick={() => setShowCreateProject(false)}>
						Abbrechen
					</Button>
					<Button
						variant="success"
						disabled={
							!showCreateProject || saving || unlockKey !== unlockCode || projectName === ""
						}
						onClick={async () => {
							setSaving(true);
							setError(undefined);

							try {
								const document = await addDoc(projectsCollectionRef, {
									Name: projectName,
									Members: {
										[user.uid]: ProjectRole.Owner,
									},
								});

								setSaving(false);
								setShowCreateProject(false);

								navigate("/projects/" + document.id);
							} catch (error) {
								if (error instanceof FirebaseError) {
									setSaving(false);

									const message = firebaseErrors[error.code] ?? error.message;

									setError(message);
									return;
								}

								throw error;
							}
						}}>
						{saving && <Spinner className="me-1" animation="border" size="sm" />}
						Speichern
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
